import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { useSheet } from "./context";

interface SheetContentProps {
  children: React.ReactNode;
  /** Side from which the sheet slides in */
  side?: "left" | "right" | "top" | "bottom";
  /** Custom className for the sheet container */
  className?: string;
  /** Callback when overlay is clicked */
  onOverlayClick?: () => void;
  /**
   * Render as a detached floating panel — adds inset spacing from viewport
   * edges, rounded corners and a drop shadow. Default false (edge-attached).
   */
  floating?: boolean;
  /**
   * Render the dim backdrop. Default true. When false the wrapper is still
   * present (so click-outside / escape still close the sheet) but no dim is
   * drawn over the page.
   */
  showOverlay?: boolean;
  /**
   * Stacking context for the sheet. Default "z-50". Lower it to sit beneath
   * dialogs that must stay on top -- AlertBox defaults to z-20 and Modal
   * hardcodes an unoverridable !z-10, so a panel that opens either one needs
   * to drop below it.
   */
  zIndexClass?: string;
  /**
   * Background utility for the panel. Default "bg-primary" (the design token;
   * identical in value to bg-white today).
   */
  background?: string;
}

/**
 * Slide duration. Kept as one constant because the CSS transition and the
 * unmount timer must agree -- as two separate literals they drift.
 */
const SHEET_ANIMATION_MS = 300;

/** Same selector the app's Dialog uses, so both traps agree on what is focusable. */
const FOCUSABLE_SELECTOR =
  'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"]), textarea:not([disabled]):not([tabindex="-1"]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])';

/**
 * Open panels, innermost last. Only the topmost traps Tab, so a sheet opened
 * from inside another sheet does not fight its parent for focus.
 */
const focusTrapStack: symbol[] = [];

/**
 * SheetContent - The content container that slides in from the side
 */
export function SheetContent({
  children,
  side = "right",
  className = "",
  onOverlayClick,
  floating = false,
  showOverlay = true,
  zIndexClass = "z-50",
  background = "bg-primary",
}: SheetContentProps) {
  const { open, close, closeOnOverlayClick } = useSheet();
  const containerRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [shouldRender, setShouldRender] = useState(open);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      // Small delay to trigger animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before unmounting
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, SHEET_ANIMATION_MS);
      return () => clearTimeout(timer);
    }
  }, [open]);

  /**
   * Focus trap.
   *
   * The panel already declared aria-modal="true", which tells assistive tech
   * that nothing outside it exists -- but nothing enforced that, so Tab walked
   * straight out into the page behind the overlay. Claiming containment
   * without providing it is worse than not claiming it at all.
   *
   * Moves focus into the panel on open, keeps Tab/Shift+Tab cycling inside it,
   * and returns focus to whatever was focused before on close.
   */
  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    const id = Symbol("sheet-focus");
    focusTrapStack.push(id);

    const focusables = () =>
      Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>(
          FOCUSABLE_SELECTOR,
        ) ?? [],
      ).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );

    // Wait for the panel to be in the DOM and animating before focusing,
    // otherwise the first element is not yet rendered.
    const focusTimer = window.setTimeout(() => {
      const items = focusables();
      if (items.length) {
        items[0].focus();
      } else {
        // No focusable children: focus the panel itself so Tab has somewhere
        // to start and the trap below still applies.
        containerRef.current?.setAttribute("tabindex", "-1");
        containerRef.current?.focus();
      }
    }, 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      // Only the innermost open panel traps.
      if (focusTrapStack[focusTrapStack.length - 1] !== id) return;

      const panel = containerRef.current;
      if (!panel) return;

      const items = focusables();
      if (!items.length) {
        event.preventDefault();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;

      // Focus outside the panel (e.g. the user clicked the page behind):
      // pull it back in rather than letting Tab wander.
      if (!panel.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      const index = focusTrapStack.indexOf(id);
      if (index !== -1) focusTrapStack.splice(index, 1);
      // Restore focus to the trigger so keyboard users are not dumped at the
      // top of the document when the panel closes.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget || e.target === overlayRef.current) {
      // Notify regardless of whether the click dismisses. That split is what
      // lets a guarded panel raise its discard prompt from a backdrop click
      // without closing underneath it.
      onOverlayClick?.();
      if (closeOnOverlayClick) close();
    }
  };

  // Animation + positioning classes based on side + floating mode
  const getSideClasses = () => {
    const baseTransition = "transition-transform duration-300 ease-in-out";

    switch (side) {
      case "left":
        return {
          container: floating
            ? `left-md top-md bottom-md ${baseTransition}`
            : `left-0 top-0 h-full ${baseTransition}`,
          open: "translate-x-0",
          closed: floating
            ? "-translate-x-[calc(100%+1rem)]"
            : "-translate-x-full",
        };
      case "right":
        return {
          container: floating
            ? `right-md top-md bottom-md ${baseTransition}`
            : `right-0 top-0 h-full ${baseTransition}`,
          open: "translate-x-0",
          closed: floating
            ? "translate-x-[calc(100%+1rem)]"
            : "translate-x-full",
        };
      case "top":
        return {
          container: floating
            ? `top-md left-md right-md ${baseTransition}`
            : `top-0 left-0 w-full ${baseTransition}`,
          open: "translate-y-0",
          closed: floating
            ? "-translate-y-[calc(100%+1rem)]"
            : "-translate-y-full",
        };
      case "bottom":
        return {
          container: floating
            ? `bottom-md left-md right-md ${baseTransition}`
            : `bottom-0 left-0 w-full ${baseTransition}`,
          open: "translate-y-0",
          closed: floating
            ? "translate-y-[calc(100%+1rem)]"
            : "translate-y-full",
        };
      default:
        return {
          container: floating
            ? `right-md top-md bottom-md ${baseTransition}`
            : `right-0 top-0 h-full ${baseTransition}`,
          open: "translate-x-0",
          closed: floating
            ? "translate-x-[calc(100%+1rem)]"
            : "translate-x-full",
        };
    }
  };

  const sideClasses = getSideClasses();
  const floatingClasses = floating
    ? "rounded-xl shadow-xl overflow-hidden"
    : "";

  if (!shouldRender) return null;

  return createPortal(
    <div
      className={`fixed inset-0 flex ${zIndexClass}`}
      role="dialog"
      aria-modal="true"
      onClick={handleOverlayClick}
    >
      {/* Overlay */}
      {showOverlay && (
        <div
          ref={overlayRef}
          className={`fixed inset-0 bg-[rgba(23,43,77,0.6)] transition-opacity duration-300 ${
            isAnimating ? "opacity-100" : "opacity-0"
          }`}
          aria-hidden="true"
        />
      )}

      {/* Sheet Container */}
      <div
        ref={containerRef}
        data-testid="panel-root"
        className={`
          fixed ${background}
          ${sideClasses.container}
          ${floatingClasses}
          ${isAnimating ? sideClasses.open : sideClasses.closed}
          ${className}
        `}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
